/**
 * The vocabulary every service plan week is written in: days, their order, and how a
 * length of time is printed.
 *
 * A file of its own rather than exports from the drawer, because the drawer opens the
 * schedule editor and the editor writes the same week in the same words — importing
 * these back out of the drawer would be a cycle.
 */

/** The sender's own 0 = Sunday encoding, which is also the order they read in. */
export const DAYS = [
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
] as const;

/**
 * The same seven days as a week a coordinator schedules, which starts on Monday.
 *
 * Derived from `DAYS` rather than written out again so the two can never disagree
 * about spelling; `DAYS` keeps its Sunday-first order because it is also the index
 * the sender's `daysOfWeek` numbers refer to.
 */
export const DAYS_MONDAY_FIRST: readonly string[] = [...DAYS.slice(1), DAYS[0]];

export function dayIndex(day: string) {
  const index = DAYS.indexOf(day.toUpperCase() as typeof DAYS[number]);
  return index === -1 ? DAYS.length : index;
}

export function title(value: string) {
  return value.charAt(0) + value.slice(1).toLowerCase();
}

/**
 * Minutes as HH:MM, the way every other duration on this screen is written.
 *
 * Hours are not wrapped at 24 — these are weekly totals, so 44:30 is a real
 * answer and 20:30 would be a wrong one. Rounded to the minute, because a weekly
 * figure derived from a monthly allowance is not a whole number of them.
 */
export function hours(minutes: number): string {
  const total = Math.round(minutes);
  const sign = total < 0 ? "-" : "";
  const absolute = Math.abs(total);
  const hh = String(Math.floor(absolute / 60)).padStart(2, "0");
  const mm = String(absolute % 60).padStart(2, "0");
  return `${sign}${hh}:${mm}`;
}

/**
 * How long a shift written as two 24-hour clock times lasts.
 *
 * Null rather than a number for anything that is not a shift — a time the browser
 * has not finished being typed into, or an end that is not after its start — so the
 * one place that has to decide what to do about it is the one that displays it.
 * A shift does not cross midnight here: the server refuses one that does.
 */
export function shiftMinutes(startTime: string, endTime: string): number | null {
  const start = minutesIntoDay(startTime);
  const end = minutesIntoDay(endTime);

  if (start === null || end === null || end <= start) {
    return null;
  }

  return end - start;
}

function minutesIntoDay(time: string): number | null {
  const match = /^(\d{1,2}):(\d{2})/.exec(time);
  return match === null ? null : Number(match[1]) * 60 + Number(match[2]);
}
